"use client";

import Image from "next/image";
import Link from "@/components/ui/CcLink";

import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import { CcCardReadMoreLabel } from "@/components/ui/CcCardReadMoreLabel";
import type { BlogListCard } from "@/lib/blog-cards";
import { BLOG_CARD_GRID_IMAGE_SIZES } from "@/components/blog/blog-card-grid-config";
import {
  getBlogPreviewCardReadLabel,
  getBlogPreviewCardReadMoreLabel,
} from "@/lib/cc-cursor-read-labels";

type CcBlogPreviewCardProps = {
  post: BlogListCard;
  titleId: string;
  headingLevel?: "h2" | "h3";
  imageSizes?: string;
  priority?: boolean;
};

function MetaDot() {
  return <span className="cc-blog-preview-card__meta-dot" aria-hidden="true" />;
}

export function CcBlogPreviewCard({
  post,
  titleId,
  headingLevel = "h3",
  imageSizes = BLOG_CARD_GRID_IMAGE_SIZES,
  priority = false,
}: CcBlogPreviewCardProps) {
  const locale = useSiteLocale();
  const readLabel = getBlogPreviewCardReadLabel(locale);
  const readMoreLabel = getBlogPreviewCardReadMoreLabel(locale);
  const TitleTag = headingLevel;
  const hasMeta = Boolean(post.date || post.readTimeText);
  const sources = post.imageSources;
  const image = <Image
    src={post.image}
    data-cc-image-source={post.image}
    alt=""
    fill
    sizes={imageSizes}
    className="cc-blog-preview-card__photo"
    unoptimized={Boolean(sources?.avif || sources?.webp)}
    loading={priority ? "eager" : "lazy"}
    fetchPriority={priority ? "high" : undefined}
  />;

  return (
    <article className="cc-blog-preview-card">
      <Link
        href={post.href}
        className="cc-blog-preview-card__link"
      >
        <div className="cc-blog-preview-card__head">
          <div className="cc-blog-preview-card__media">
            {sources?.avif || sources?.webp ? <picture>
              {sources.avif ? <source type="image/avif" srcSet={sources.avif} sizes={imageSizes} /> : null}
              {sources.webp ? <source type="image/webp" srcSet={sources.webp} sizes={imageSizes} /> : null}
              {image}
            </picture> : image}
            <span className="cc-blog-preview-card__overlay" aria-hidden="true" />
            <span className="cc-blog-preview-card__overlay-label" aria-hidden="true">
              {readLabel}
            </span>
          </div>
          <div className="cc-blog-preview-card__summary">
            <TitleTag id={titleId} className="cc-blog-preview-card__title">
              {post.title}
            </TitleTag>
          </div>
        </div>
        <div className="cc-blog-preview-card__body">
          {hasMeta ? (
            <>
              <span className="cc-blog-preview-card__line" aria-hidden="true" />
              <div className="cc-blog-preview-card__meta">
                {post.date ? (
                  <time className="cc-blog-preview-card__date" dateTime={post.dateTime}>
                    {post.date}
                  </time>
                ) : null}
                {post.readTimeText ? (
                  <>
                    {post.date ? <MetaDot /> : null}
                    <span className="cc-blog-preview-card__read-time">{post.readTimeText}</span>
                  </>
                ) : null}
              </div>
            </>
          ) : null}
          <CcCardReadMoreLabel className="cc-blog-preview-card__read-more">
            {readMoreLabel}
          </CcCardReadMoreLabel>
        </div>
      </Link>
    </article>
  );
}
